import { useCallback, useEffect, useState } from "react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import {
  CreditCard, DollarSign, Receipt, TrendingUp, Calendar, RefreshCw, Settings as Cog, AlertCircle,
} from "lucide-react";

const fmt = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const FEE_FILTERS = [
  { key: "all",          label: "All payments" },
  { key: "registration", label: "Registration" },
  { key: "monthly",      label: "Monthly" },
];
const STATUS_FILTERS = [
  { key: "all",     label: "Any status" },
  { key: "paid",    label: "Paid" },
  { key: "pending", label: "Pending" },
  { key: "failed",  label: "Failed" },
];

const MembershipFeesPage = () => {
  const { user } = useAuth();
  const isSuperuser = user?.role === "superuser";

  const [config, setConfig] = useState(null);
  const [summary, setSummary] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feeFilter, setFeeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, sum] = await Promise.all([
        api.get("/membership-fees/config"),
        api.get("/membership-fees/summary"),
      ]);
      setConfig(cfg.data);
      setSummary(sum.data);
      const params = new URLSearchParams();
      if (feeFilter    !== "all") params.append("fee_type", feeFilter);
      if (statusFilter !== "all") params.append("status",   statusFilter);
      const list = await api.get(`/membership-fees/list?${params.toString()}`);
      setPayments(list.data.payments);
    } catch (e) {
      toast.error("Failed to load membership fees");
    } finally {
      setLoading(false);
    }
  }, [feeFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const saveConfig = async () => {
    if (!isSuperuser) return;
    setSaving(true);
    try {
      const r = await api.put("/membership-fees/config", config);
      setConfig({
        ...config,
        registration_fee: r.data.registration_fee,
        monthly_fee:      r.data.monthly_fee,
        enabled:          r.data.membership_fees_enabled,
        description:      r.data.membership_fees_description,
      });
      toast.success("Membership fees updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout
      title="Membership Fees"
      subtitle="Stripe-collected membership revenue · separate from investment-capital flows"
    >
      {!isSuperuser && (
        <Card className="border-amber-200 bg-amber-50 mb-4">
          <CardContent className="p-4 flex items-center gap-2">
            <AlertCircle size={16} className="text-amber-600" />
            <p className="text-sm text-amber-900">
              You can view all data here, but only superusers can change pricing.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="membership-summary-tiles">
        <StatTile icon={DollarSign}    label="Total revenue (paid)"          value={fmt(summary?.total_revenue)} testid="stat-total-revenue" />
        <StatTile icon={TrendingUp}    label="Last 30 days"                  value={fmt(summary?.last_30_days?.total)} sub={`${summary?.last_30_days?.count || 0} payments`} testid="stat-last-30" />
        <StatTile icon={Receipt}       label="Registration paid"             value={fmt(summary?.registration?.paid)} sub={`${summary?.registration?.paid_count || 0} ✓ · ${summary?.registration?.pending_count || 0} pending`} testid="stat-registration" />
        <StatTile icon={Calendar}      label="Monthly paid"                  value={fmt(summary?.monthly?.paid)} sub={`${summary?.monthly?.paid_count || 0} ✓ · ${summary?.monthly?.pending_count || 0} pending`} testid="stat-monthly" />
      </div>

      {/* Pricing config card (superuser write, admin read) */}
      <Card className="mb-6" data-testid="membership-config-card">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <Cog size={16} className="text-blue-600" />
            <h2 className="text-lg font-semibold text-slate-900">Pricing</h2>
            <span className="text-xs text-slate-500 ml-auto">Currency: USD</span>
          </div>
          {!config ? (
            <div className="h-24 bg-slate-100 animate-pulse rounded" />
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="reg-fee" className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Registration fee (one-time)</Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                    <Input id="reg-fee" type="number" min="0" step="1"
                      disabled={!isSuperuser}
                      value={config.registration_fee}
                      onChange={(e) => setConfig({ ...config, registration_fee: parseFloat(e.target.value || 0) })}
                      className="pl-7"
                      data-testid="config-registration-fee" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="mon-fee" className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Monthly membership fee</Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                    <Input id="mon-fee" type="number" min="0" step="1"
                      disabled={!isSuperuser}
                      value={config.monthly_fee}
                      onChange={(e) => setConfig({ ...config, monthly_fee: parseFloat(e.target.value || 0) })}
                      className="pl-7"
                      data-testid="config-monthly-fee" />
                  </div>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Description (shown to clients)</Label>
                <Textarea rows={2}
                  disabled={!isSuperuser}
                  value={config.description || ""}
                  onChange={(e) => setConfig({ ...config, description: e.target.value })}
                  placeholder="e.g. Unlocks portal access, monthly market intelligence reports, and priority support."
                  className="mt-1"
                  data-testid="config-description" />
              </div>
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-3 text-sm text-slate-700">
                  <Switch checked={!!config.enabled}
                    disabled={!isSuperuser}
                    onCheckedChange={(v) => setConfig({ ...config, enabled: v })}
                    data-testid="config-enabled" />
                  Membership fee collection {config.enabled ? "enabled" : "disabled"}
                </label>
                {isSuperuser && (
                  <Button onClick={saveConfig} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="config-save">
                    {saving ? "Saving…" : "Save pricing"}
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Filters + ledger */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Tabs value={feeFilter} onValueChange={setFeeFilter}>
          <TabsList className="bg-slate-100">
            {FEE_FILTERS.map((f) => (
              <TabsTrigger key={f.key} value={f.key} data-testid={`fee-filter-${f.key}`}>{f.label}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <Tabs value={statusFilter} onValueChange={setStatusFilter}>
          <TabsList className="bg-slate-100">
            {STATUS_FILTERS.map((f) => (
              <TabsTrigger key={f.key} value={f.key} data-testid={`status-filter-${f.key}`}>{f.label}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <Button onClick={load} variant="outline" size="sm" className="ml-auto gap-1.5" data-testid="ledger-refresh">
          <RefreshCw size={14} /> Refresh
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center text-slate-400 text-sm">Loading…</div>
          ) : payments.length === 0 ? (
            <div className="p-12 text-center text-slate-400 text-sm" data-testid="ledger-empty">No payments match these filters.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="ledger-table">
                <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-4 py-3 font-semibold">User</th>
                    <th className="px-4 py-3 font-semibold">Type</th>
                    <th className="px-4 py-3 font-semibold text-right">Amount</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3 font-semibold">Created</th>
                    <th className="px-4 py-3 font-semibold">Paid at</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {payments.map((p) => (
                    <tr key={p.id} className="hover:bg-slate-50" data-testid={`ledger-row-${p.id}`}>
                      <td className="px-4 py-3">
                        <p className="text-sm font-semibold text-slate-900">{p.user_full_name || "—"}</p>
                        <p className="text-xs text-slate-500">{p.user_email}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700 capitalize">{p.fee_type}</td>
                      <td className="px-4 py-3 text-sm text-right font-mono font-semibold text-slate-900">{fmt(p.amount)}</td>
                      <td className="px-4 py-3">
                        <Badge className={
                          p.status === "paid"    ? "bg-emerald-50 text-emerald-700 border-emerald-200 border" :
                          p.status === "pending" ? "bg-amber-50 text-amber-700 border-amber-200 border" :
                                                   "bg-rose-50 text-rose-700 border-rose-200 border"
                        }>{p.status}</Badge>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500">{p.created_at?.slice(0, 10)}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{p.paid_at?.slice(0, 10) || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  );
};

const StatTile = ({ icon: Icon, label, value, sub, testid }) => (
  <Card data-testid={testid}>
    <CardContent className="p-4 flex items-center gap-3">
      <div className="h-10 w-10 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center shrink-0">
        <Icon size={18} />
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-900">{value}</p>
        <p className="text-xs text-slate-500">{label}</p>
        {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
      </div>
    </CardContent>
  </Card>
);

export default MembershipFeesPage;
